// Turns one rollup payload into the same three results the dashboard consumes:
// KPI metrics, trend series, and drill rows.
//
// The payload covers BOTH windows at once -- planSources merges the current and
// prior-year weeks into one fetch -- so every consumer here re-splits it per
// window first. Getting that wrong would put prior-year rows into the current
// total, which is the kind of mistake that produces a plausible number.

import { useMemo } from "react";
import type { FilterState, Granularity, ScopeFilters } from "@/types/filters";
import type { ComputedMetrics } from "@/types/metrics";
import { deriveMetrics } from "@/lib/metrics";
import { makeTotalsReader, type DvmWeekRow, type RollupSiteWeekRow } from "@/lib/rollupMetrics";
import { buildDrillFromRollup } from "@/lib/rollupDrill";
import { splitRange } from "@/lib/rollupSplit";
import { EMPTY_RANGE, getPeriodFamily, getPriorYearRange, type DateRange } from "@/lib/periods";
import type { RollupPayload } from "@/hooks/useRollupData";
import type { SchedulingRow } from "@/types/scheduling";
import {
  ancestorLabel,
  buildPartialScope,
  pinnedAncestorLevels,
  type DrillRow,
  type PinnedRow,
} from "@/hooks/useDrillData";

const NO_SCOPE: ScopeFilters = {
  countryCode: null,
  divisionName: null,
  groupName: null,
  locationId: null,
  resourceId: null,
};

// Narrows a payload that spans both windows down to one of them.
function forWindow(
  payload: RollupPayload,
  range: DateRange,
  granularity: Granularity,
  family: "trailing" | "calendar"
): { siteWeeks: RollupSiteWeekRow[]; dvmWeeks: DvmWeekRow[]; edgeRows: SchedulingRow[] } {
  const split = splitRange(range, granularity, family);
  const weeks = new Set(split.wholeWeeks);
  const days = new Set(split.edgeDays);
  return {
    siteWeeks: payload.siteWeeks.filter((r) => weeks.has(String(r.week_start))),
    dvmWeeks: payload.dvmWeeks.filter((r) => weeks.has(String(r.week_start))),
    edgeRows: payload.edgeRows.filter((r) => days.has(r.schedule_date)),
  };
}

export interface RollupComputed {
  metrics: { current: ComputedMetrics; priorYear: ComputedMetrics; nvaAvg: ComputedMetrics };
  drill: { rows: DrillRow[]; pinned: PinnedRow[] };
}

export function useRollupComputed(
  payload: RollupPayload | null,
  filters: FilterState,
  granularity: Granularity
): RollupComputed | null {
  // Destructured outside the memo so the deps can name the fields read here. This
  // hook inherited the whole-object dependency from useMetrics, so a display-only
  // toggle rebuilt every mask aggregation too -- and in compare mode that happened
  // alongside the raw path, doubling the cost.
  const {
    customStart,
    customEnd,
    period,
    scope,
    appointmentTypes,
    excludeZeroApptDays,
    drillLevel,
  } = filters;
  return useMemo(() => {
    if (!payload) return null;

    const family = getPeriodFamily(period);
    const current: DateRange = { startDate: customStart, endDate: customEnd };
    const priorYear = getPriorYearRange(current, period) ?? EMPTY_RANGE;

    // The split MUST match the granularity the payload was fetched with, not a
    // fixed "week". The server plans its sources from the requested granularity,
    // so a Day request returns raw rows and no rollup rows at all -- and asking
    // for the week split then discards every one of them, because a whole week
    // has no edge days. Compare mode caught exactly that: T1W (day-only) reported
    // 27 mismatched KPI fields while the trend matched.
    //
    // Using the fetched granularity is safe because routing rule 5 guarantees the
    // plan covers every day of both windows exactly once, however it divides them.
    // The KPI cards want all the rows; they do not care how they arrived.
    const curKpi = forWindow(payload, current, granularity, family);
    const pyKpi = forWindow(payload, priorYear, granularity, family);

    // makeTotalsReader owns the wide-vs-narrow rule -- which measures a total at
    // a given scope is allowed to read. See rollupMetrics.ts.
    const totals = (part: ReturnType<typeof forWindow>, s: ScopeFilters) =>
      makeTotalsReader({
        part,
        payloadScope: scope,
        wideDvmRows: payload.wideDvmRows,
        excludeZeroApptDays,
      })(s);

    const metrics = {
      current: deriveMetrics(totals(curKpi, scope), customStart, customEnd, appointmentTypes),
      priorYear: deriveMetrics(
        totals(pyKpi, scope),
        priorYear.startDate,
        priorYear.endDate,
        appointmentTypes
      ),
      // Unscoped, matching useMetrics: the benchmark is every eligible site over
      // the same dates.
      nvaAvg: deriveMetrics(totals(curKpi, NO_SCOPE), customStart, customEnd, appointmentTypes),
    };

    // The trend lives in useRollupTrend, called once per chart. A series can only
    // be built from a payload fetched at its OWN granularity -- see that file.

    // Pinned ancestor rows. Which levels appear, how each partial scope is built
    // and what each row is called all come from useDrillData rather than being
    // restated here: two copies would drift, and a drifted copy shows up as an
    // average that exists on one path and not the other.
    //
    // Only the totals differ, and only in source. An ancestor row is the current
    // window re-totalled under a widened scope, which the rollup path does
    // directly -- buildTotalsFromRollup applies scope client-side exactly as
    // filterRowsByScope does for raw rows. siteCount comes back from that same
    // call, so it counts sites that SURVIVED the filter rather than sites that
    // merely have a row; recomputing it here would be the second implementation
    // of a rule that took two bugs to get right.
    const pinned: PinnedRow[] = pinnedAncestorLevels(drillLevel, scope).map((level) => {
      const ancestorTotals = totals(curKpi, buildPartialScope(scope, level));
      return {
        label: ancestorLabel(level, scope),
        siteCount: ancestorTotals.siteCount,
        metrics: deriveMetrics(ancestorTotals, customStart, customEnd, appointmentTypes),
      };
    });

    const drillEntities = buildDrillFromRollup({
      ...curKpi,
      scope,
      drillLevel: drillLevel,
      excludeZeroApptDays,
    });
    const drill = {
      rows: drillEntities
        .map((e) => ({
          id: e.id,
          label: e.label,
          siteCount: e.siteCount,
          metrics: deriveMetrics(e.totals, customStart, customEnd, appointmentTypes),
        }))
        .sort((a, b) => b.metrics.totalScheduledMin - a.metrics.totalScheduledMin),
      pinned,
    };

    return { metrics, drill };
    // Specific fields, not the whole object. This hook inherited the pattern from
    // useMetrics, so a display-only toggle rebuilt every mask aggregation too --
    // and in compare mode that happened alongside the raw path, doubling the cost.
  }, [
    payload,
    granularity,
    excludeZeroApptDays,
    customStart,
    customEnd,
    period,
    scope,
    appointmentTypes,
    drillLevel,
  ]);
}
